import { useState } from 'react';
import styles from '../../pages/MeetingReport.module.css';

export default function CommentsVsQuestionsChart({ inquiry, nameMap = {}, printMode = false }) {
  const [mode, setMode] = useState('perc'); // 'count' | 'perc'

  const data = inquiry.perPerson.map(p => {
    const total = p.inquiry + p.advocacy;
    return {
      name: nameMap[p.name] || p.name.split('@')[0],
      Questions: mode === 'count' ? p.inquiry : total ? Math.round((p.inquiry / total) * 100) : 0,
      Comments: mode === 'count' ? p.advocacy : total ? Math.round((p.advocacy / total) * 100) : 0,
      total,
    };
  }).sort((a, b) => b.Questions - a.Questions);

  const maxVal = mode === 'count'
    ? Math.max(...data.map(d => d.Questions + d.Comments), 1)
    : 100;

  // Print cards are a fixed height regardless of team size, so rows must shrink to fit
  // rather than overflowing the card — the print report has no scroll.
  const PRINT_ROWS_BUDGET = 130;
  const printRowHeight = Math.max(Math.floor(PRINT_ROWS_BUDGET / data.length) - 4, 12);
  const BAR_HEIGHT = printMode ? Math.min(printRowHeight, 28) : 28;
  const GAP = printMode ? Math.max(Math.min(printRowHeight - BAR_HEIGHT + 4, 12), 3) : 12;
  const chartHeight = data.length * (BAR_HEIGHT + GAP) + 40;
  const LABEL_WIDTH = 80;

  return (
    <div className={styles.cvsqContainer} style={printMode ? { marginBottom: 0 } : undefined}>
      {!printMode && (
        <div className={styles.chartHeaderRow}>
          <div className="report-section-header" style={{ margin: 0 }}>Comments vs Questions</div>
          <div className={styles.toggleRow}>
            <span style={{ color: mode === 'count' ? 'var(--navy)' : 'var(--ink-muted)', fontWeight: mode === 'count' ? 700 : 400 }}>Count</span>
            <div
              className={styles.toggleTrack}
              onClick={() => setMode(m => m === 'count' ? 'perc' : 'count')}
              style={{ background: mode === 'perc' ? 'var(--navy)' : 'var(--border)' }}
            >
              <div className={styles.toggleThumb} style={{ left: mode === 'perc' ? 21 : 3 }} />
            </div>
            <span style={{ color: mode === 'perc' ? 'var(--navy)' : 'var(--ink-muted)', fontWeight: mode === 'perc' ? 700 : 400 }}>Perc.</span>
          </div>
        </div>
      )}
      {!printMode && (
        <p className={styles.chartDescLg}>
          Total {mode === 'count' ? 'count' : 'percentage'} of comments and questions per participant
        </p>
      )}

      {/* Chart */}
      <div style={{ position: 'relative' }}>
        {mode === 'perc' && (
          <div style={{
            position: 'absolute',
            left: `calc(50% + ${(LABEL_WIDTH + 10) / 2}px)`,
            top: 0, bottom: 0,
            width: 0,
            borderLeft: '2px dashed #5f7942',
            pointerEvents: 'none',
            zIndex: 1,
          }}>
            <span style={{
              position: 'absolute', top: -18, left: 4,
              fontSize: '0.68rem', color: '#5f7942', whiteSpace: 'nowrap',
            }}>
              Suggested Balance
            </span>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
          {data.map(row => {
          const questionsPx = (row.Questions / maxVal);
          const commentsPx = (row.Comments / maxVal);
          return (
            <div key={row.name} className={styles.barRow}>
              {/* Name label */}
              <div className={styles.barLabel} style={{ width: LABEL_WIDTH }}>
                {row.name}
              </div>

              {/* Bars */}
              <div className={styles.barBarsCol} style={{ height: BAR_HEIGHT }}>
                {/* Questions bar */}
                {row.Questions > 0 && (
                  <div style={{
                    width: `${questionsPx * 100}%`,
                    height: '100%',
                    background: '#5f7942',
                    borderRadius: commentsPx === 0 ? '4px' : '4px 0 0 4px',
                    minWidth: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    paddingRight: 6,
                    boxSizing: 'border-box',
                  }}>
                    {questionsPx > 0.08 && (
                      <span className={styles.barValueWhite}>
                        {row.Questions}{mode === 'perc' ? '%' : ''}
                      </span>
                    )}
                  </div>
                )}
                {/* Comments bar */}
                {row.Comments > 0 && (
                  <div style={{
                    width: `${commentsPx * 100}%`,
                    height: '100%',
                    background: '#d0d0d8',
                    borderRadius: questionsPx === 0 ? '4px' : '0 4px 4px 0',
                    minWidth: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    paddingRight: 6,
                    boxSizing: 'border-box',
                  }}>
                    {commentsPx > 0.08 && (
                      <span className={styles.barValueDark}>
                        {row.Comments}{mode === 'perc' ? '%' : ''}
                      </span>
                    )}
                  </div>
                )}
                {/* End label */}
                <span className={styles.barEndLabel}>
                  {mode === 'count' ? row.total : ''}
                </span>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* X-axis ticks */}
      <div className={styles.xAxisTicks} style={{ marginLeft: LABEL_WIDTH + 10 }}>
        {[0, 0.25, 0.5, 0.75, 1].map(f => (
          <div key={f} style={{
            position: 'absolute', left: `${f * 100}%`,
            fontSize: '0.68rem', color: 'var(--ink-muted)',
            transform: 'translateX(-50%)',
          }}>
            {mode === 'count' ? Math.round(maxVal * f) : `${Math.round(f * 100)}%`}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className={styles.cvsqLegend} style={{ marginLeft: LABEL_WIDTH + 10 }}>
        <div className={styles.legendItem}>
          <div style={{ width: 12, height: 12, borderRadius: 2, background: '#5f7942' }} />
          Questions
        </div>
        <div className={styles.legendItem}>
          <div style={{ width: 12, height: 12, borderRadius: 2, background: '#d0d0d8' }} />
          Comments
        </div>
      </div>
    </div>
  );
}
